import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';

const CLERK_ENABLED = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

const MARITIME_DOMAINS = [
  'maersk', 'msc', 'cmacgm', 'hapag', 'evergreen', 'cosco', 'yangming',
  'hmm', 'zim', 'wan-hai', 'oocl', 'pil', 'torm', 'stena', 'norden',
  'euronav', 'frontline', 'teekay', 'scorpio', 'genco', 'eagle-bulk',
  'star-bulk', 'safe-bulkers', 'navios', 'diana', 'grindrod', 'oldendorff',
  'berge', 'klaveness', 'gearbulk', 'swire', 'pacific-basin', 'anglo-eastern',
  'v-ships', 'fleet-management', 'synergy', 'thome', 'columbia',
  'bernhard-schulte', 'bw-group', 'hafnia', 'ardmore', 'dorian',
  'international-seaways', 'dht', 'hunter-group',
];

const MARITIME_KEYWORDS = [
  'shipping', 'maritime', 'marine', 'vessel', 'tanker', 'bulk', 'charter',
  'freight', 'navigation', 'port', 'cargo', 'offshore', 'lng', 'lpg',
  'bunker', 'ship-management', 'crewing',
];

function checkQualification(user) {
  const linkedinAccount = user.externalAccounts?.find(
    a => a.provider === 'oauth_linkedin_oidc'
  );
  const emailDomain = user.primaryEmailAddress?.emailAddress?.split('@')[1]?.toLowerCase() ?? '';
  const companyName = (linkedinAccount?.label ?? '').toLowerCase();

  const domainInEmail = MARITIME_DOMAINS.find(d => emailDomain.includes(d));
  if (domainInEmail) return { isQualified: true, matchReason: `domain:${domainInEmail}` };

  const domainInCompany = MARITIME_DOMAINS.find(d => companyName.includes(d));
  if (domainInCompany) return { isQualified: true, matchReason: `domain:${domainInCompany}` };

  const keywordInCompany = MARITIME_KEYWORDS.find(k => companyName.includes(k));
  if (keywordInCompany) return { isQualified: true, matchReason: `keyword:${keywordInCompany}` };

  return { isQualified: false, matchReason: 'none' };
}

export default function useQualifiedLead() {
  const firedRef = useRef(false);

  if (!CLERK_ENABLED) return;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { user, isLoaded, isSignedIn } = useUser();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user || firedRef.current) return;

    const posthog = window.__posthog;
    if (!posthog) return;

    const flagKey = `fv_lead_tagged_${user.id}`;
    if (localStorage.getItem(flagKey)) {
      firedRef.current = true;
      return;
    }

    const linkedinAccount = user.externalAccounts?.find(
      a => a.provider === 'oauth_linkedin_oidc'
    );
    const emailDomain = user.primaryEmailAddress?.emailAddress?.split('@')[1]?.toLowerCase() ?? null;
    const { isQualified, matchReason } = checkQualification(user);

    posthog.identify(user.id, {
      name: user.fullName,
      email: user.primaryEmailAddress?.emailAddress,
      email_domain: emailDomain,
      auth_provider: 'linkedin',
      is_qualified_lead: isQualified,
      qualification_match: matchReason,
      first_seen: new Date().toISOString(),
      linkedin_vanity_url: linkedinAccount?.username ?? null,
      company_from_linkedin: linkedinAccount?.label ?? null,
    });

    posthog.capture('user_signed_up', {
      provider: 'linkedin',
      is_qualified_lead: isQualified,
      qualification_match: matchReason,
    });

    localStorage.setItem(flagKey, '1');
    firedRef.current = true;
  }, [isLoaded, isSignedIn, user]);
}
